import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Lock, LogOut, Inbox, Users, RefreshCw, Trash2, Phone, Mail, MapPin,
  HeartHandshake, Clock, ArrowLeft
} from 'lucide-react';
import {
  adminLogin, adminVerify, getConsultations, getCareers, getStats,
  updateConsultationStatus, updateCareerStatus, deleteConsultation, deleteCareer,
} from '../api';

const CONSULT_STATUS = ['new', 'contacted', 'scheduled', 'closed'];
const CAREER_STATUS = ['new', 'reviewing', 'interviewing', 'hired', 'archived'];

const statusColor = (s) => {
  const map = {
    new: '#5B8C4E', contacted: '#C08A2D', scheduled: '#3A6FB0', closed: '#8A8A80',
    reviewing: '#C08A2D', interviewing: '#3A6FB0', hired: '#5B8C4E', archived: '#8A8A80',
  };
  return map[s] || '#5B8C4E';
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
};

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [tab, setTab] = useState('consultations');
  const [consultations, setConsultations] = useState([]);
  const [careers, setCareers] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => { document.body.style.backgroundColor = 'var(--cream-2)'; return () => { document.body.style.backgroundColor = 'var(--cream)'; }; }, []);

  const loadAll = useCallback(async () => {
    try {
      const [c, j, s] = await Promise.all([getConsultations(), getCareers(), getStats()]);
      setConsultations(c.data); setCareers(j.data); setStats(s.data);
    } catch (e) { /* handled by verify */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('mbf_admin_token');
    if (!token) { setChecking(false); return; }
    adminVerify().then(() => { setAuthed(true); return loadAll(); })
      .catch(() => { localStorage.removeItem('mbf_admin_token'); })
      .finally(() => setChecking(false));
  }, [loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await adminLogin(password);
      localStorage.setItem('mbf_admin_token', res.data.token);
      setAuthed(true);
      await loadAll();
    } catch (err) {
      setError('Incorrect password. Please try again.');
    } finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem('mbf_admin_token'); setAuthed(false); setPassword(''); };

  const changeConsult = async (id, status) => { await updateConsultationStatus(id, status); loadAll(); };
  const changeCareer = async (id, status) => { await updateCareerStatus(id, status); loadAll(); };
  const removeConsult = async (id) => { if (window.confirm('Delete this request?')) { await deleteConsultation(id); loadAll(); } };
  const removeCareer = async (id) => { if (window.confirm('Delete this application?')) { await deleteCareer(id); loadAll(); } };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center" style={{ color: 'var(--muted)' }}>Loading\u2026</div>;
  }

  // ---------- Login ----------
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-[420px] bg-white rounded-3xl p-8 lg:p-10" style={{ border: '1px solid var(--line)' }}>
          <span className="flex items-center justify-center rounded-full mx-auto mb-6" style={{ width: 64, height: 64, backgroundColor: 'var(--sage)' }}>
            <Lock size={28} style={{ color: 'var(--forest)' }} />
          </span>
          <h1 className="text-2xl text-center">Staff Sign In</h1>
          <p className="text-center mt-2 text-[15px]" style={{ color: 'var(--muted)' }}>Enter the shared password to view the inbox.</p>
          <form onSubmit={login} className="mt-7 flex flex-col gap-4">
            <input type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="admin-inp" />
            <button type="submit" disabled={loading} className="btn-sage justify-center" style={{ opacity: loading ? 0.7 : 1 }}>{loading ? 'Signing in\u2026' : 'Sign In'}</button>
            {error && <p className="text-sm text-center" style={{ color: '#B4614A' }}>{error}</p>}
          </form>
          <Link to="/" className="flex items-center justify-center gap-2 mt-6 text-sm" style={{ color: 'var(--muted)' }}><ArrowLeft size={15} /> Back to website</Link>
        </div>
        <style>{`.admin-inp { width:100%; padding:13px 16px; border-radius:12px; border:1px solid var(--line); background:var(--cream); font-size:15px; outline:none; }
          .admin-inp:focus { border-color: var(--forest); box-shadow: 0 0 0 3px rgba(30,59,44,0.1); }`}</style>
      </div>
    );
  }

  // ---------- Dashboard ----------
  const data = tab === 'consultations' ? consultations : careers;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20" style={{ backgroundColor: 'var(--forest)' }}>
        <div className="container-x flex items-center justify-between h-[68px]">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center rounded-full" style={{ width: 36, height: 36, backgroundColor: 'var(--sage)' }}>
              <HeartHandshake size={20} style={{ color: 'var(--forest)' }} />
            </span>
            <span className="text-white font-serif-display text-lg" style={{ color: '#fff' }}>MBF Admin Inbox</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadAll} className="flex items-center gap-2 text-white/80 hover:text-white text-sm"><RefreshCw size={16} /> Refresh</button>
            <button onClick={logout} className="flex items-center gap-2 text-white/80 hover:text-white text-sm"><LogOut size={16} /> Sign Out</button>
          </div>
        </div>
      </header>

      <div className="container-x py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="New Consultations" value={stats?.consultations?.new ?? 0} sub={`${stats?.consultations?.total ?? 0} total`} icon={Inbox} />
          <StatCard label="New Applications" value={stats?.careers?.new ?? 0} sub={`${stats?.careers?.total ?? 0} total`} icon={Users} />
          <StatCard label="Total Consultations" value={stats?.consultations?.total ?? 0} icon={Inbox} muted />
          <StatCard label="Total Applications" value={stats?.careers?.total ?? 0} icon={Users} muted />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[['consultations', 'Consultation Requests', consultations.length], ['careers', 'Caregiver Applications', careers.length]].map(([key, label, count]) => (
            <button key={key} onClick={() => setTab(key)} className="px-5 py-2.5 rounded-full font-medium text-[15px] transition-all"
              style={{ backgroundColor: tab === key ? 'var(--forest)' : '#fff', color: tab === key ? '#fff' : 'var(--ink)', border: `1px solid ${tab === key ? 'var(--forest)' : 'var(--line)'}` }}>
              {label} <span style={{ opacity: 0.7 }}>({count})</span>
            </button>
          ))}
        </div>

        {/* List */}
        {data.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 text-center" style={{ border: '1px solid var(--line)' }}>
            <Inbox size={40} style={{ color: 'var(--muted)' }} className="mx-auto mb-4" />
            <p style={{ color: 'var(--muted)' }}>No {tab === 'consultations' ? 'consultation requests' : 'applications'} yet.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {tab === 'consultations'
              ? consultations.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl p-6" style={{ border: '1px solid var(--line)' }}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg">{c.name}</h3>
                        <span className="text-xs px-2.5 py-1 rounded-full text-white font-medium" style={{ backgroundColor: statusColor(c.status) }}>{c.status}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-[14px]" style={{ color: 'var(--muted)' }}>
                        <span className="flex items-center gap-1.5"><Phone size={14} /> {c.phone}</span>
                        {c.email && <span className="flex items-center gap-1.5"><Mail size={14} /> {c.email}</span>}
                        {c.careType && <span className="flex items-center gap-1.5"><HeartHandshake size={14} /> {c.careType}</span>}
                        <span className="flex items-center gap-1.5"><Clock size={14} /> {fmtDate(c.createdAt)}</span>
                      </div>
                      {c.notes && <p className="mt-3 text-[15px] p-3 rounded-xl" style={{ backgroundColor: 'var(--cream-2)', color: 'var(--ink)' }}>{c.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={c.status} onChange={(e) => changeConsult(c.id, e.target.value)} className="admin-select">
                        {CONSULT_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => removeConsult(c.id)} className="flex items-center justify-center rounded-lg" style={{ width: 38, height: 38, border: '1px solid var(--line)', color: '#B4614A' }}><Trash2 size={17} /></button>
                    </div>
                  </div>
                </div>
              ))
              : careers.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl p-6" style={{ border: '1px solid var(--line)' }}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg">{c.fullName}</h3>
                        <span className="text-xs px-2.5 py-1 rounded-full text-white font-medium" style={{ backgroundColor: statusColor(c.status) }}>{c.status}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-[14px]" style={{ color: 'var(--muted)' }}>
                        <span className="flex items-center gap-1.5"><Phone size={14} /> {c.phone}</span>
                        {c.email && <span className="flex items-center gap-1.5"><Mail size={14} /> {c.email}</span>}
                        {c.city && <span className="flex items-center gap-1.5"><MapPin size={14} /> {c.city}</span>}
                        <span className="flex items-center gap-1.5"><Clock size={14} /> {fmtDate(c.createdAt)}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-2 text-[14px]" style={{ color: 'var(--ink)' }}>
                        {c.yearsExperience && <span><strong>Experience:</strong> {c.yearsExperience}</span>}
                        {c.availability && <span><strong>Availability:</strong> {c.availability}</span>}
                        {c.certifications && <span><strong>Certs:</strong> {c.certifications}</span>}
                      </div>
                      {c.notes && <p className="mt-3 text-[15px] p-3 rounded-xl" style={{ backgroundColor: 'var(--cream-2)', color: 'var(--ink)' }}>{c.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <select value={c.status} onChange={(e) => changeCareer(c.id, e.target.value)} className="admin-select">
                        {CAREER_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => removeCareer(c.id)} className="flex items-center justify-center rounded-lg" style={{ width: 38, height: 38, border: '1px solid var(--line)', color: '#B4614A' }}><Trash2 size={17} /></button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      <style>{`.admin-select { padding: 9px 12px; border-radius: 10px; border: 1px solid var(--line); background: var(--cream); font-size: 14px; text-transform: capitalize; outline: none; cursor: pointer; }
        .admin-select:focus { border-color: var(--forest); }`}</style>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, muted }) {
  return (
    <div className="bg-white rounded-2xl p-5" style={{ border: '1px solid var(--line)' }}>
      <div className="flex items-center justify-between">
        <span className="text-sm" style={{ color: 'var(--muted)' }}>{label}</span>
        <Icon size={18} style={{ color: muted ? 'var(--muted)' : 'var(--forest)' }} />
      </div>
      <div className="font-serif-display text-3xl mt-2" style={{ color: 'var(--forest)' }}>{value}</div>
      {sub && <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{sub}</div>}
    </div>
  );
}
